import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useCompletionStatus } from '@/hooks/general/useCompletionStatus';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { useRegistrationDetailsQuery } from '@/hooks/queries/registration/generalRegistrationQueries';
import { usePersonalDetails } from '@/hooks/forms/register/usePersonalDetails';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IAllAccountData,
    IAccountPersonalDetails,
} from '@/types/types/forms/account/account.interface';
import { accountService } from '@/services/api/accountService/accountService';
import { usePhrasesStore } from '@/store/phrasesStore';
import { getAccountLocale } from '@/utils/locale/getAccountLocale';
import { useQueryParams } from '@/hooks/general/useQueryParams';
import { AccountPersonalDetailsFormInputs } from './AccountPersonalDetailsFormInputs';
import { Grid } from '@mui/material';
import { ACCOUNT_GENERAL_PHRASES } from '@/consts/phrases/accountPhrases';
import { SaveButtonWithCompletionStatus } from '@/components/general/buttons/SaveButton/SaveButtonWithCompletionStatus';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

interface PersonalDetailsFormProps {
    accountData?: IAllAccountData;
    dictionary: DefaultPageProps['dictionary'];
}

export const AccountPersonalDetailsForm: FC<PersonalDetailsFormProps> = ({
    accountData,
    dictionary,
}) => {
    const { getCompletionStatus, resetCompletionStatus, completionStatus } = useCompletionStatus();
    const router = useRouter();
    const { hubinKey } = useQueryParams();
    const { statesList, preferredLangList } = usePersonalDetails({
        dictionary,
        accountData,
    });

    const { control, handleSubmit, formState, getValues, setValue, setError, clearErrors, watch } =
        useForm<Partial<IAccountPersonalDetails>>({
            mode: 'onChange',
            values: {
                firstName: accountData?.firstName,
                lastName: accountData?.lastName,
                countryCode: accountData?.countryCode,
                zip: accountData?.zip,
                stateCode: accountData?.stateCode,
                city: accountData?.city,
                address1: accountData?.address1,
                address2: accountData?.address2,
                timezoneId: accountData?.timezoneId,
                preferredLanguageId: accountData?.preferredLanguageId,
                marketResearch: accountData?.marketResearch,
            },
            context: {
                stateCodeExist: Array.isArray(statesList),
                preferredLanguageIdExist: Array.isArray(preferredLangList),
            },
        });
    const { setLocale } = usePhrasesStore();

    const { revalidate: revalidateRegistrationDetails } = useRegistrationDetailsQuery();
    const applyAccountLocale = (
        countryCode: string | undefined,
        preferredLanguageId: number | null | undefined,
    ) => {
        if (!countryCode) return;
        const locale = getAccountLocale(preferredLanguageId, countryCode, dictionary?.countries);
        setLocale(locale);
    };

    const { serverErrors, handleServerErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const { errors, isSubmitting } = formState;

    const submitData = async (values: Partial<IAccountPersonalDetails>) => {
        try {
            const resp = await getCompletionStatus(() =>
                accountService.savePersonalDetails(values, hubinKey),
            );
            applyAccountLocale(values.countryCode, values.preferredLanguageId);
            await revalidateRegistrationDetails();
            clearAllErrors();
            if (resp.surveyUrl) router.push(resp.surveyUrl);
        } catch (err) {
            handleServerErrors(err);
        }
    };

    return (
        <form onSubmit={handleSubmit(submitData)} noValidate>
            <FormGridErrors errors={serverErrors} gridSx={{ mb: 2 }} generalErrorId="message_box" />
            <AccountPersonalDetailsFormInputs
                control={control}
                dictionary={dictionary}
                accountData={accountData}
                isReadOnly={false}
                errors={errors}
                setValue={setValue}
                watch={watch}
            />
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <SaveButtonWithCompletionStatus
                    id="submit-personal-details"
                    label={ACCOUNT_GENERAL_PHRASES.SAVE_BUTTON}
                    isSubmitting={isSubmitting}
                    status={completionStatus}
                    resetStatus={resetCompletionStatus}
                />
            </Grid>
        </form>
    );
};
